import {
  Button,
  Input,
  InputAmount,
  InputSelect,
  InputWrapper,
} from '@vrplatform/components';
import { useInfiniteQuery, useQuery, useTeam } from '@vrplatform/data';
import type { SelectItem } from '@vrplatform/components';
import type { accountReservationRevRec_enum } from '@vrplatform/graphql';
import { isUUID } from '@vrplatform/utils';
import { Box, Group, Modal } from '@mantine/core';
import { whereRecurringFees } from '@vrplatform/ui-common';
import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { FEE_TYPES } from '../../../views/fees/overview/_components';
import { getFeeTypeRouteFromRecurringFeeEnum } from '../../../views/fees/overview/_utils';
import {
  type ReservationFeeAddInputs,
  useReservationFeeMutation,
} from '../_hooks';
import type { Reservation } from '../_queries';
import { ReservationPostingDateSelectInput } from './ReservationPostingDateSelectInput';
import { getPostingDatePlaceholder } from './ReservationPostingDateSelectInput.utils';

const trapSpacesForRequiredFields = (value: string) =>
  !!value.trim() || 'This field is required';

type RecurringFeeSelectItem = SelectItem & {
  revenueRecognition: accountReservationRevRec_enum | null;
};

type Props = {
  reservation: {
    id: string;
    lock?: Reservation['lock'];
    bookedAt?: Reservation['bookedAt'];
    checkIn?: Reservation['checkIn'];
    checkOut?: Reservation['checkOut'];
  };
  closeModal: () => void;
  recurringFee?:
    | (ReservationFeeAddInputs & {
        id?: string;
        lineTypeLabel?: string;
      })
    | null;
  zIndex?: number;
};

export const ReservationFeeAddModal = ({
  reservation,
  closeModal: cl,
  recurringFee: initial,
  zIndex,
}: Props) => {
  const opened = !!initial;
  const isEditMode = !!initial?.id;
  const [team] = useTeam();
  const teamId = team?.id;
  const defaultRevenueRecognition = team?.defaultRevenueRecognition;
  const [search, setSearch] = useState('');
  const [selectedRecurringFee, setSelectedRecurringFee] = useState<
    RecurringFeeSelectItem | undefined | null
  >(undefined);

  const methods = useForm<ReservationFeeAddInputs>({
    defaultValues: {
      centTotal: undefined,
      description: '',
      recurringFeeId: null,
      postingAt: null,
    },
  });
  const recurringFeeId = useWatch({
    control: methods.control,
    name: 'recurringFeeId',
  });

  const recurringFeesQuery = useInfiniteQuery(
    (q, args, { limit, offset }) => {
      const where = whereRecurringFees({
        tenantId: args.teamId,
        search: args.search,
      });

      const aggregate =
        q
          .recurringFeeAggregate({
            where,
          })
          ?.aggregate?.count() || 0;

      const list = q
        .recurringFees({
          where,
          limit,
          offset,
          order_by: [
            {
              type: 'asc_nulls_last',
            },
            {
              title: 'asc_nulls_last',
            },
          ],
        })
        .map<RecurringFeeSelectItem>((fee) => {
          return {
            label: fee.title || '-',
            value: fee.id,
            group:
              FEE_TYPES[
                getFeeTypeRouteFromRecurringFeeEnum(fee.type ?? 'additionalFee')
              ].title,
            revenueRecognition: fee.revenueRecognition ?? null,
          };
        });

      return {
        aggregate,
        list,
      };
    },
    {
      queryKey: ['customFees'],
      variables: {
        search: search?.trim(),
        teamId,
      },
    }
  );

  const initialRecurringFeeQuery = useQuery(
    (q, args) => {
      if (!args.recurringFeeId || !isUUID(args.recurringFeeId)) return null;

      return (
        q
          .recurringFees({
            where: {
              id: {
                _eq: args.recurringFeeId,
              },
            },
            limit: 1,
          })
          .map<RecurringFeeSelectItem>((fee) => {
            return {
              label: fee.title || '-',
              value: fee.id,
              revenueRecognition: fee.revenueRecognition ?? null,
            };
          })[0] ?? null
      );
    },
    {
      queryKey: ['customFees'],
      variables: {
        recurringFeeId,
      },
    }
  );

  const recurringFeeValue = useMemo(() => {
    if (!recurringFeeId) return null;

    if (selectedRecurringFee !== undefined) return selectedRecurringFee;

    return initialRecurringFeeQuery.data ?? null;
  }, [initialRecurringFeeQuery.data, recurringFeeId, selectedRecurringFee]);

  const postingDatePlaceholder = useMemo(() => {
    return getPostingDatePlaceholder(
      recurringFeeValue?.revenueRecognition ?? defaultRevenueRecognition
    );
  }, [defaultRevenueRecognition, recurringFeeValue?.revenueRecognition]);
  const lineTypeLabel =
    initial?.lineTypeLabel ?? recurringFeeValue?.label ?? '';

  useEffect(() => {
    methods.reset(
      initial
        ? {
            centTotal: initial.centTotal,
            description: initial.description,
            recurringFeeId: initial.recurringFeeId,
            postingAt: initial.postingAt ?? null,
          }
        : {
            centTotal: undefined,
            description: '',
            recurringFeeId: null,
            postingAt: null,
          }
    );
  }, [initial, methods]);

  const closeModal = () => {
    cl();
    setSearch('');
    setSelectedRecurringFee(undefined);
    methods.reset({
      centTotal: undefined,
      description: '',
      recurringFeeId: null,
      postingAt: null,
    });
  };

  const { submit } = useReservationFeeMutation(reservation.id, closeModal);

  return (
    <Modal
      opened={opened}
      onClose={closeModal}
      title={isEditMode ? 'Edit fee adjustment' : 'Add fee adjustment'}
      styles={{
        title: { fontWeight: 500 },
      }}
      zIndex={zIndex}
      centered
    >
      <Box
        sx={(theme) => ({
          display: 'flex',
          flexDirection: 'column',
          gap: theme.spacing.md,
        })}
      >
        <Controller
          control={methods.control}
          rules={{
            required: 'Description is required',
            validate: trapSpacesForRequiredFields,
          }}
          name="description"
          render={({ field, fieldState }) => (
            <InputWrapper
              label="Description"
              error={fieldState.error?.message}
              required
            >
              <Input
                value={field.value}
                setValue={field.onChange}
                error={!!fieldState.error}
                placeholder={'Fee Adjustment'}
              />
            </InputWrapper>
          )}
        />

        {!isEditMode ? (
          <Controller
            control={methods.control}
            rules={{
              required: 'Please select a recurring fee',
            }}
            name="recurringFeeId"
            render={({ field, fieldState }) => (
              <InputWrapper
                label="Fee & Commission"
                error={fieldState.error?.message}
                required
              >
                <InputSelect
                  value={recurringFeeValue}
                  setValue={(value) => {
                    field.onChange(value?.value ?? null);
                    setSelectedRecurringFee(
                      value as RecurringFeeSelectItem | null
                    );
                  }}
                  type="single"
                  inputProps={{
                    error: !!fieldState.error,
                    placeholder: 'Select fee',
                    withClearButton: true,
                    loadingQuery: initialRecurringFeeQuery.isLoading,
                    disabled: !!initial?.recurringFeeId,
                  }}
                  infiniteData={{ ...recurringFeesQuery, setSearch }}
                />
              </InputWrapper>
            )}
          />
        ) : (
          <InputWrapper label="Line type">
            <Input value={lineTypeLabel} setValue={() => {}} disabled />
          </InputWrapper>
        )}

        {reservation.lock?.booksClosed?.date && (
          <ReservationPostingDateSelectInput
            control={methods.control}
            name="postingAt"
            placeholder={postingDatePlaceholder}
            reservationDates={{
              bookedAt: reservation.bookedAt,
              checkIn: reservation.checkIn,
              checkOut: reservation.checkOut,
            }}
            forcePostingDate={reservation.lock?.status === 'locked'}
            minDate={reservation.lock?.booksClosed?.date}
            description="This date defines the journal entry date for this financial"
          />
        )}

        <Controller
          control={methods.control}
          rules={{
            validate: (value) => {
              if (typeof value !== 'number') {
                return 'Please enter a number';
              }

              if (value === 0) {
                return 'Please enter a non-zero amount';
              }

              return true;
            },
          }}
          name="centTotal"
          render={({ field, fieldState }) => (
            <InputWrapper
              label="Amount"
              error={fieldState.error?.message}
              required
            >
              <InputAmount
                value={
                  field.value === undefined
                    ? ''
                    : Number(field.value || '0') / 100
                }
                setValue={(value) => field.onChange(Number(value || 0) * 100)}
                decimalScale={2}
                error={!!fieldState.error?.message}
                placeholder="0.00"
                leftSection={<span>$</span>}
              />
            </InputWrapper>
          )}
        />

        {!reservation.lock?.booksClosed?.date && (
          <ReservationPostingDateSelectInput
            control={methods.control}
            name="postingAt"
            placeholder={postingDatePlaceholder}
            reservationDates={{
              bookedAt: reservation.bookedAt,
              checkIn: reservation.checkIn,
              checkOut: reservation.checkOut,
            }}
            forcePostingDate={reservation.lock?.status === 'locked'}
            minDate={reservation.lock?.booksClosed?.date}
          />
        )}

        <Group justify="right" mt="md">
          <Button
            onClick={closeModal}
            disabled={methods.formState.isSubmitting}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={methods.handleSubmit(
              async (data) =>
                await submit(
                  isEditMode && initial?.id
                    ? { data, lineId: initial.id, type: 'edit' }
                    : { data, type: 'add' }
                ),
              (err) => console.error(err)
            )}
            loading={methods.formState.isSubmitting}
          >
            {isEditMode ? 'Save adjustment' : 'Add adjustment'}
          </Button>
        </Group>
      </Box>
    </Modal>
  );
};
